/**
 * /meet/:code — the meeting itself, for colleagues and guests alike.
 *
 * Mounted OUTSIDE the app Layout: a guest has no sidebar, and a colleague in
 * a video call does not want one either. Leaving this page is leaving the
 * meeting, which is also what hangs up any voice channel (the voice provider
 * lives in the Layout and unmounts with it).
 *
 * Phases: lobby (name, mic/camera choice) → joining → waiting (guests, until
 * a colleague admits them) → room → ended | denied. The transcript is
 * recorded from our own microphone exactly as in voice channels, and shown
 * live to colleagues only.
 */
import { apiBase } from '../lib/apiBase';
import { Captions, Disc, Loader2, Mic, MicOff, Video, VideoOff, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MeetControls, type MeetPanel } from '../components/meet/MeetControls';
import { MeetSidePanel } from '../components/meet/MeetSidePanel';
import { CopyLinkButton, CopyTextButton, meetHistoryPath } from '../components/meet/MeetingCard';
import { VideoGrid, type MeetLayout } from '../components/meet/VideoGrid';
import { useTestMode } from '../components/testMode/TestModeContext';
import { LiveBadge } from '../components/teamChat/VoiceChannelRow';
import { useI18n } from '../contexts/I18nContext';
import {
  useAdmission,
  useAdmitPrompt,
  useMeetPublicInfo,
  useModeration,
  useWaitingRoom,
} from '../hooks/useMeet';
import { AdmitRequestOverlay } from '../components/meet/AdmitRequestOverlay';
import { useMeetChat } from '../hooks/useMeetChat';
import { useMeetRoom } from '../hooks/useMeetRoom';
import { canRecordMeeting, useMeetRecorder } from '../hooks/useMeetRecorder';
import { useLiveTranscript } from '../hooks/useVoiceCalls';
import { ApiError } from '../lib/api';
import {
  endMeeting,
  fetchMeetSegmentsSince,
  fetchMeetSessionState,
  joinMeet,
  leaveMeet,
  uploadMeetChunk,
  type MeetJoinResult,
  type MeetParticipant,
} from '../lib/meetApi';
import {
  canTranscribeLocally,
  startMicTranscriber,
  type MicTranscriberHandle,
  type TranscriberStopReason,
} from '../lib/voiceTranscriber';
import { useAuthStore } from '../store/useAuthStore';
import { meetingDetailsText } from '../lib/meetDetails';

type Phase = 'lobby' | 'joining' | 'waiting' | 'room' | 'ended' | 'denied';

type LobbyError =
  | 'meet.lobby.notOpen'
  | 'meet.lobby.full'
  | 'meet.lobby.ended'
  | 'meet.lobby.removed'
  | 'meet.lobby.notConfigured'
  | 'meet.error.connect';

/** A stable empty list, so the admit prompt's effect does not re-run every render. */
const EMPTY_WAITING: MeetParticipant[] = [];
const WAITING_POLL_MS = 3_000;

function formatRecTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const whenFormat = new Intl.DateTimeFormat('ro-RO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});
const timeFormat = new Intl.DateTimeFormat('ro-RO', { hour: '2-digit', minute: '2-digit' });

function lobbyErrorFor(err: unknown): LobbyError {
  if (err instanceof ApiError) {
    if (err.code === 'MEETING_NOT_OPEN') return 'meet.lobby.notOpen';
    if (err.code === 'MEETING_FULL') return 'meet.lobby.full';
    if (err.code === 'MEETING_ENDED') return 'meet.lobby.ended';
    if (err.code === 'MEETING_REMOVED') return 'meet.lobby.removed';
    if (err.status === 503) return 'meet.lobby.notConfigured';
  }
  return 'meet.error.connect';
}

/** Best-effort "I left" on tab close: keepalive so the browser finishes it. */
function leaveOnUnload(token: string): void {
  try {
    void fetch(`${apiBase()}/api/meet/session/leave`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Nothing else to do; the stale sweep closes the meeting.
  }
}

const LAYOUT_STORAGE_KEY = 'meet.layout';

/**
 * Pin the page scale to 1 while the call page is mounted.
 *
 * A call is a full-screen surface with nothing to read up close, and a call
 * page that arrives magnified — iOS does that after any tap in a form, and
 * keeps it — is one where the top bar and half the controls are off screen.
 * Google Meet and Zoom lock the scale on their call pages for the same reason.
 * Capping `maximum-scale` also snaps a page that is already zoomed back to 1,
 * which is the whole point for someone coming in from the lobby.
 *
 * Scoped to this page only: the rest of the app keeps pinch-zoom, which is an
 * accessibility feature. The original content is put back on unmount.
 */
function usePinnedViewportScale(): void {
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.content;
    meta.content = `${original}, maximum-scale=1`;
    return () => {
      meta.content = original;
    };
  }, []);
}

function readLayout(): MeetLayout {
  try {
    return localStorage.getItem(LAYOUT_STORAGE_KEY) === 'grid' ? 'grid' : 'speaker';
  } catch {
    return 'speaker';
  }
}

/** The Firmio wordmark, linking out to the product site in a new tab. */
function FirmioMark({ className = 'h-7' }: { className?: string }) {
  return (
    <a
      href="https://firmio.ro"
      target="_blank"
      rel="noopener noreferrer"
      title="Firmio"
      className="inline-flex shrink-0 items-center"
    >
      <img
        src="/brand/firmio-logo-horizontal-on-dark.svg"
        alt="Firmio"
        className={`${className} w-auto`}
      />
    </a>
  );
}

/**
 * The page fills the viewport exactly and never scrolls as a whole: the stage
 * takes what is left between the header and the controls, and only the side
 * panel scrolls. `h-dvh` follows the phone's collapsing address bar.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background text-foreground">
      {children}
    </div>
  );
}

function CenterCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-4 py-6">
      <FirmioMark className="mb-6 h-9" />
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-2xl">
        {children}
      </div>
    </div>
  );
}

export function MeetRoomPage() {
  usePinnedViewportScale();
  const { t, locale } = useI18n();
  const { code = '' } = useParams<{ code: string }>();
  const me = useAuthStore((s) => s.user);
  const initializing = useAuthStore((s) => s.initializing);
  const { enabled: testMode } = useTestMode();
  const { data: info, isLoading, error: infoError } = useMeetPublicInfo(code || undefined);
  const room = useMeetRoom();
  const admission = useAdmission();
  const moderation = useModeration();

  const [phase, setPhase] = useState<Phase>('lobby');
  const [session, setSession] = useState<MeetJoinResult | null>(null);
  const meetingIdForRecorder = session?.meeting.id ?? null;
  const recorder = useMeetRecorder(room.getRoom, meetingIdForRecorder);
  const recording = recorder.status === 'recording' || recorder.status === 'stopping';
  const toggleRecording = () => {
    if (recording) {
      void recorder.stop().then(() => room.announceRecording(false));
    } else {
      void recorder.start().then(() => room.announceRecording(true));
    }
  };
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [micPref, setMicPref] = useState(true);
  const [camPref, setCamPref] = useState(true);
  const [panel, setPanel] = useState<MeetPanel>(null);
  const [layout, setLayout] = useState<MeetLayout>(readLayout);
  const [lobbyError, setLobbyError] = useState<LobbyError | null>(null);
  const [hostInside, setHostInside] = useState(true);
  const [transcriptStopped, setTranscriptStopped] = useState<string | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  const transcriberRef = useRef<MicTranscriberHandle | null>(null);
  const leftRef = useRef(false);

  const isColleague = Boolean(session && !session.me.guest);
  // Only whoever opened the meeting gets the red "close for everyone" button.
  const isHost = Boolean(
    session && session.me.userId && session.me.userId === session.meeting.hostId,
  );

  const toggleLayout = () => {
    const next: MeetLayout = layout === 'grid' ? 'speaker' : 'grid';
    setLayout(next);
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, next);
    } catch {
      // A private window; the choice lasts for this meeting only.
    }
  };
  const token = session?.participantToken ?? null;
  const meetingId = session?.meeting.id ?? null;

  // --- transcript (colleagues only) -----------------------------------------
  const fetchSince = useCallback(
    (_id: string, since: string | null) => fetchMeetSegmentsSince(token ?? '', since),
    [token],
  );
  const live = useLiveTranscript({
    callId: isColleague && phase === 'room' ? meetingId : null,
    active: isColleague && phase === 'room',
    fetchSince,
  });

  // --- chat (everyone in the room, guests included) ---------------------------
  const chat = useMeetChat({
    token,
    active: phase === 'room',
    panelOpen: panel === 'chat',
    getRoom: room.getRoom,
    roomStatus: room.status,
  });

  // --- waiting room (colleagues only) ----------------------------------------
  const { data: waiting } = useWaitingRoom(meetingId, isColleague && phase === 'room');
  const waitingList = waiting ?? EMPTY_WAITING;
  // Every colleague in the call can admit, so every one of them gets the prompt.
  const admitPrompt = useAdmitPrompt(meetingId, waitingList, isColleague && phase === 'room');

  // --- the recorder ----------------------------------------------------------
  const startTranscriber = useCallback(
    (joined: MeetJoinResult) => {
      const sdkRoom = room.getRoom();
      if (!sdkRoom || !joined.transcriptionEnabled) return;
      if (!canTranscribeLocally()) {
        setTranscriptStopped('teamChat.voiceTranscriptUnsupported');
        return;
      }
      const onStopped = (reason: TranscriberStopReason) => {
        transcriberRef.current = null;
        setTranscribing(false);
        if (reason === 'budget') setTranscriptStopped('teamChat.voiceTranscriptBudget');
        else if (reason === 'unsupported')
          setTranscriptStopped('teamChat.voiceTranscriptUnsupported');
        else if (reason === 'upstream') setTranscriptStopped('teamChat.voiceTranscriptUpstream');
        else if (reason !== 'call-ended') setTranscriptStopped('teamChat.voiceTranscriptStopped');
      };
      transcriberRef.current = startMicTranscriber({
        room: sdkRoom,
        upload: (chunk) => uploadMeetChunk({ token: joined.participantToken, ...chunk }),
        onStopped,
      });
      setTranscribing(true);
    },
    [room],
  );

  const enterRoom = useCallback(
    async (joined: MeetJoinResult, grant: NonNullable<MeetJoinResult['livekit']>) => {
      setPhase('room');
      await room.connect(grant, { mic: micPref, camera: camPref });
      startTranscriber(joined);
    },
    [camPref, micPref, room, startTranscriber],
  );

  // --- leaving ---------------------------------------------------------------
  const leave = useCallback(async () => {
    if (leftRef.current) return;
    leftRef.current = true;
    const transcriber = transcriberRef.current;
    transcriberRef.current = null;
    setTranscribing(false);
    await room.leave();
    if (transcriber) await transcriber.stop().catch(() => undefined);
    if (token) await leaveMeet(token).catch(() => undefined);
    setPhase('ended');
  }, [room, token]);

  const endForAll = useCallback(async () => {
    if (!meetingId) return;
    try {
      await endMeeting(meetingId);
    } catch {
      // The room deletion still reaches us as a disconnect; nothing to show.
    }
    await leave();
  }, [leave, meetingId]);

  // The host closed the room, or the connection died for good.
  useEffect(() => {
    if (phase !== 'room') return;
    if (room.status === 'ended') void leave();
  }, [leave, phase, room.status]);

  useEffect(() => {
    if (!token) return;
    const handler = () => leaveOnUnload(token);
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [token]);

  // --- the waiting-room poll -------------------------------------------------
  useEffect(() => {
    if (phase !== 'waiting' || !session) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const state = await fetchMeetSessionState(session.participantToken);
        if (cancelled) return;
        setHostInside(state.hostInside);
        if (state.meetingStatus === 'ENDED' || state.meetingStatus === 'CANCELLED') {
          setPhase('ended');
        } else if (state.admission === 'DENIED') {
          setPhase('denied');
        } else if (state.admission === 'ADMITTED' && state.livekit) {
          await enterRoom(session, state.livekit);
        }
      } catch {
        // Retried on the next tick.
      }
    };
    void poll();
    const timer = setInterval(() => void poll(), WAITING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enterRoom, phase, session]);

  // --- joining ---------------------------------------------------------------
  const join = async () => {
    if (testMode) return;
    setLobbyError(null);
    setPhase('joining');
    try {
      const joined = await joinMeet(
        code,
        me ? {} : { name: name.trim(), email: email.trim().toLowerCase() },
      );
      setSession(joined);
      leftRef.current = false;
      if (joined.admission === 'ADMITTED' && joined.livekit) {
        await enterRoom(joined, joined.livekit);
      } else {
        setPhase('waiting');
      }
    } catch (err) {
      setLobbyError(lobbyErrorFor(err));
      setPhase('lobby');
    }
  };

  useEffect(() => {
    return () => {
      void transcriberRef.current?.stop();
    };
  }, []);

  // ---------------------------------------------------------------------------

  if (isLoading || initializing) {
    return (
      <Shell>
        <CenterCard>
          <p className="flex items-center gap-2 text-sm text-foreground">
            <Loader2 size={16} className="animate-spin" />
            {t('common.loading')}
          </p>
        </CenterCard>
      </Shell>
    );
  }

  if (!info || infoError) {
    return (
      <Shell>
        <CenterCard>
          <h1 className="text-lg font-semibold">{t('meet.notFound')}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('meet.lobby.checkLink')}</p>
          {me && (
            <Link to="/meet" className="mt-4 inline-block text-sm text-emerald-400 hover:underline">
              {t('meet.back')}
            </Link>
          )}
        </CenterCard>
      </Shell>
    );
  }

  // --- ended / denied ----------------------------------------------------------
  if (
    phase === 'ended' ||
    phase === 'denied' ||
    info.status === 'ENDED' ||
    info.status === 'CANCELLED'
  ) {
    const denied = phase === 'denied';
    return (
      <Shell>
        <CenterCard>
          <h1 className="text-lg font-semibold">{info.title}</h1>
          <p className="mt-2 text-sm text-foreground">
            {denied
              ? t('meet.lobby.denied')
              : room.removedByHost
                ? t('meet.lobby.removed')
                : info.status === 'CANCELLED'
                  ? t('meet.lobby.cancelled')
                  : info.status === 'ENDED' || room.endedByHost
                    ? t('meet.lobby.ended')
                    : t('meet.lobby.left')}
          </p>
          {me ? (
            <div className="mt-5 flex flex-wrap gap-2">
              {session && (
                <Link
                  to={meetHistoryPath(session.meeting.id)}
                  className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-fixed-white hover:bg-emerald-700"
                >
                  {t('meet.viewTranscript')}
                </Link>
              )}
              <Link
                to="/meet"
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
              >
                {t('meet.backToApp')}
              </Link>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">{t('meet.lobby.thanks')}</p>
          )}
        </CenterCard>
      </Shell>
    );
  }

  // --- waiting room -------------------------------------------------------------
  if (phase === 'waiting' || phase === 'joining') {
    return (
      <Shell>
        <CenterCard>
          <h1 className="text-lg font-semibold">{info.title}</h1>
          <p className="mt-4 flex items-center gap-2 text-sm text-foreground">
            <Loader2 size={16} className="animate-spin text-amber-400" />
            {phase === 'joining' ? t('meet.lobby.joining') : t('meet.lobby.waiting')}
          </p>
          {phase === 'waiting' && !hostInside && (
            <p className="mt-2 text-sm text-muted-foreground">{t('meet.lobby.hostNotIn')}</p>
          )}
          <p className="mt-4 text-xs text-muted-foreground">{t('meet.lobby.transcribedNotice')}</p>
          <button
            type="button"
            onClick={() => void leave()}
            className="mt-5 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            {t('meet.controls.leave')}
          </button>
        </CenterCard>
      </Shell>
    );
  }

  // --- in the room ----------------------------------------------------------------
  if (phase === 'room' && session) {
    const canAdmit = isColleague;
    const errorKey = room.error;
    return (
      <Shell>
        <header className="flex shrink-0 items-center gap-3 border-b border-border px-3 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] sm:px-4">
          <FirmioMark className="h-6 sm:h-7" />
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 truncate text-sm font-semibold">
              <span className="truncate">{info.title}</span>
              <LiveBadge />
            </h1>
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              {t('meet.participants', { count: room.members.length })}
              {room.status === 'reconnecting' && (
                <span className="text-amber-400">{t('teamChat.voiceReconnecting')}</span>
              )}
              {transcribing && (
                <span
                  title={t('meet.transcript.hint')}
                  className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-medium text-emerald-400"
                >
                  <Captions size={12} />
                  {t('teamChat.voiceTranscriptOn')}
                </span>
              )}
              {(recording || room.recordingBy) && (
                <span
                  title={
                    recording
                      ? t('meet.recording.you', { seconds: recorder.seconds })
                      : t('meet.recording.by', { name: room.recordingBy ?? '' })
                  }
                  className="inline-flex items-center gap-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] font-medium text-red-400"
                >
                  <Disc size={12} className="animate-pulse" />
                  {recording
                    ? t('meet.recording.badge', { time: formatRecTime(recorder.seconds) })
                    : t('meet.recording.by', { name: room.recordingBy ?? '' })}
                </span>
              )}
              {recorder.error && (
                <button
                  type="button"
                  onClick={recorder.dismissError}
                  className="text-red-400 underline-offset-2 hover:underline"
                >
                  {t(recorder.error)}
                </button>
              )}
            </p>
          </div>
          {isColleague && <CopyLinkButton url={`${window.location.origin}/meet/${code}`} compact />}
        </header>

        {errorKey && (
          <div className="flex items-center gap-2 border-b border-red-500/40 bg-red-500/10 px-4 py-1.5 text-sm text-red-300">
            <span className="flex-1">{t(errorKey)}</span>
            <button
              type="button"
              onClick={room.dismissError}
              aria-label={t('common.close')}
              className="rounded p-1 hover:bg-red-500/20"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="flex min-h-0 flex-1">
          <main className="min-h-0 min-w-0 flex-1 p-2 sm:p-3">
            {room.status === 'connecting' ? (
              <p className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" />
                {t('teamChat.voiceConnecting')}
              </p>
            ) : (
              <VideoGrid
                members={room.members}
                speakingIds={room.speakingIds}
                screenShares={room.screenShares}
                layout={layout}
              />
            )}
          </main>
          {panel && (
            <div className="fixed inset-0 z-40 bg-background md:static md:inset-auto md:z-auto md:bg-transparent">
              <MeetSidePanel
                panel={panel}
                members={room.members}
                speakingIds={room.speakingIds}
                waiting={waitingList}
                canAdmit={canAdmit}
                onAdmit={(pid) =>
                  admission.mutate({
                    meetingId: session.meeting.id,
                    participantId: pid,
                    decision: 'admit',
                  })
                }
                onDeny={(pid) =>
                  admission.mutate({
                    meetingId: session.meeting.id,
                    participantId: pid,
                    decision: 'deny',
                  })
                }
                canModerate={isColleague}
                canRemove={isHost}
                onModerate={(pid, action) =>
                  moderation.mutate({ meetingId: session.meeting.id, participantId: pid, action })
                }
                segments={live.segments}
                meParticipantId={session.participantId}
                transcriptionEnabled={session.transcriptionEnabled}
                transcriptStopped={transcriptStopped}
                chat={chat.messages}
                chatSending={chat.sending}
                chatError={chat.error}
                onSendChat={chat.send}
                onClose={() => setPanel(null)}
              />
            </div>
          )}
        </div>

        {canAdmit && admitPrompt.prompt && (
          <AdmitRequestOverlay
            prompt={admitPrompt.prompt}
            meetingTitle={info.title}
            onAccept={admitPrompt.accept}
            onDeny={admitPrompt.deny}
            onLater={admitPrompt.later}
            onAcceptAll={admitPrompt.acceptAll}
          />
        )}

        <footer className="shrink-0 border-t border-border pb-[env(safe-area-inset-bottom)]">
          <MeetControls
            micEnabled={room.micEnabled}
            cameraEnabled={room.cameraEnabled}
            screenShareEnabled={room.screenShareEnabled}
            panel={panel}
            waitingCount={canAdmit ? waitingList.length : 0}
            chatUnread={chat.unread}
            showTranscript={isColleague}
            canEndForAll={isHost}
            // Anyone with a Firmio account can record, not just the host; while
            // a colleague is already recording the button hides for the others
            // (starting a second one would close theirs on the server).
            canRecord={isColleague && canRecordMeeting() && (recording || !room.recordingBy)}
            recording={recording}
            onToggleRecording={toggleRecording}
            layout={layout}
            showLayoutToggle={room.members.length >= 3 && room.screenShares.length === 0}
            onToggleLayout={toggleLayout}
            onToggleMic={() => void room.toggleMic()}
            onToggleCamera={() => void room.toggleCamera()}
            onToggleScreenShare={() => void room.toggleScreenShare()}
            onTogglePanel={(p) => setPanel((current) => (current === p ? null : p))}
            onLeave={() => void leave()}
            onEndForAll={() => void endForAll()}
          />
        </footer>
        {/* Remote audio, outside React's re-renders (see VoiceRoomContext). */}
        <div ref={room.audioRef} aria-hidden className="sr-only" />
      </Shell>
    );
  }

  // --- lobby ------------------------------------------------------------------------
  const opensAt = new Date(info.opensAt);
  return (
    <Shell>
      <CenterCard>
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-400">
          Firmio Meet
        </p>
        <h1 className="mt-1 text-xl font-semibold">{info.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {whenFormat.format(new Date(info.scheduledAt))} · {info.durationMinutes} min ·{' '}
          {t('meet.hostedBy', { name: info.hostName })}
        </p>
        {/* Everything above as text, for pasting into a chat or an email. */}
        <div className="mt-3">
          <CopyTextButton
            text={meetingDetailsText(
              info,
              `${window.location.origin}/meet/${encodeURIComponent(code)}`,
              t,
              locale,
            )}
            label={t('meet.copyDetails')}
          />
        </div>
        {info.status === 'LIVE' && (
          <p className="mt-2 flex items-center gap-2 text-sm text-foreground">
            <LiveBadge />
            {t('meet.participants', { count: info.participantCount })}
          </p>
        )}

        <div className="mt-5 space-y-4">
          {me ? (
            <p className="text-sm text-foreground">
              {t('meet.lobby.joiningAs', { name: me.name })}
            </p>
          ) : (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                {t('meet.lobby.name')}
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('meet.lobby.namePlaceholder')}
                autoFocus
                maxLength={60}
                className="w-full rounded-md border border-border bg-accent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
              />
              <label className="mb-1 mt-3 block text-xs font-medium text-muted-foreground">
                {t('meet.lobby.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('meet.lobby.emailPlaceholder')}
                maxLength={120}
                autoComplete="email"
                className="w-full rounded-md border border-border bg-accent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-muted-foreground">{t('meet.lobby.emailNotice')}</p>
              {/* A colleague on a device (or the other domain) where they are not
                  signed in would otherwise knock as a guest and wait to be let
                  in. Signing in brings them back here, straight into the room,
                  able to admit others. */}
              <p className="mt-3 text-xs text-muted-foreground">
                {t('meet.lobby.colleagueSignIn')}{' '}
                <Link
                  to="/login"
                  state={{ from: { pathname: `/meet/${code}` } }}
                  className="font-medium text-emerald-400 hover:underline"
                >
                  {t('meet.lobby.signIn')}
                </Link>
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMicPref((v) => !v)}
              aria-pressed={!micPref}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                micPref ? 'bg-accent text-foreground' : 'bg-red-500/20 text-red-300'
              }`}
            >
              {micPref ? <Mic size={16} /> : <MicOff size={16} />}
              {micPref ? t('meet.lobby.micOn') : t('meet.lobby.micOff')}
            </button>
            <button
              type="button"
              onClick={() => setCamPref((v) => !v)}
              aria-pressed={!camPref}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                camPref ? 'bg-accent text-foreground' : 'bg-red-500/20 text-red-300'
              }`}
            >
              {camPref ? <Video size={16} /> : <VideoOff size={16} />}
              {camPref ? t('meet.lobby.camOn') : t('meet.lobby.camOff')}
            </button>
          </div>

          {!info.joinable && (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
              {t('meet.lobby.opensAt', { time: timeFormat.format(opensAt) })}
            </p>
          )}
          {lobbyError && (
            <p className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {t(lobbyError)}
            </p>
          )}
          {testMode && <p className="text-sm text-amber-300">{t('meet.testMode')}</p>}
          {!info.livekitConfigured && (
            <p className="text-sm text-red-300">{t('meet.lobby.notConfigured')}</p>
          )}

          <button
            type="button"
            onClick={() => void join()}
            disabled={
              testMode ||
              !info.joinable ||
              !info.livekitConfigured ||
              (!me && (name.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())))
            }
            className="w-full rounded-md bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-fixed-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {me ? t('meet.lobby.join') : t('meet.lobby.askToJoin')}
          </button>

          {info.transcriptionEnabled && (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Captions size={14} className="mt-0.5 shrink-0" />
              {t('meet.lobby.transcribedNotice')}
            </p>
          )}
        </div>
      </CenterCard>
    </Shell>
  );
}
